import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';

import BackToTop from '../components/BackToTop';

const PageTemplate = css`
  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const KBShortcuts: React.FunctionComponent = () => (
  <IndexLayout>
    <Helmet>
      <title>Keyboard Shortcuts for Adobe Photoshop | Giggster Guide</title>
      <meta
        name="description"
        content="An easy Photoshop keyboard shortcut reference to help speed up your retouching workflow."
      />
    </Helmet>
    <Wrapper css={PageTemplate}>
      <header css={[outer, SiteHeader]}>
        <div css={inner}>
          <SiteNav />
        </div>
      </header>
      <main id="site-main" className="site-main" css={[SiteMain, outer]}>
        <article className="post page" css={[PostFull, NoImage]}>
          <PostFullHeader>
            <PostFullTitle>Keyboard Shortcuts for Adobe Photoshop ⌨️</PostFullTitle>
          </PostFullHeader>

          <PostFullContent className="post-full-content">
            <div className="post-content">
              <p>
                A list of some of the most common &amp; useful Photoshop keyboard shortcuts. Getting
                to know them is a worthwhile investment and saves a ton of time over the long haul.
              </p>

              <p>
                <span className="show-for-small-only">
                  PC users, replace <span className="key">Opt</span> with{' '}
                  <span className="key">Alt</span>, and replace <span className="key">Cmd</span>{' '}
                  with <span className="key">Ctrl</span>.
                </span>
              </p>

              <h2 className="text-center">Tools</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="move-tool">
                      Move<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">V</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="marquee">
                      Marquee<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">M</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span>
                      Lasso<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">L</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="magic-wand">
                      Magic Wand<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">W</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="crop">
                      Crop<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">C</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="eyedropper">
                      Eyedropper<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">I</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="healing">
                      Healing Brush<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">J</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="brush">
                      Brush<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">B</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="stamp">
                      Clone Stamp<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">I</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span>
                      History Brush<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">Y</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="eraser">
                      Eraser<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">E</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="gradient">
                      Gradient<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">G</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span>
                      Dodge<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">O</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="pen">
                      Pen<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">P</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="text-tool">
                      Type<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">T</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="selection">
                      Path Selection<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">A</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="shape">
                      Shape<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">U</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="hand">
                      Hand<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">H</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-12 columns text-center">
                  <h4>
                    <span className="zoom">
                      Zoom<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">Z</span>
                </div>
              </div>

              <hr />

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Default Colors</h4>
                  <span className="key">D</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Flip Colors</h4>
                  <span className="key">X</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Quick Mask</h4>
                  <span className="key">Q</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Full Screen</h4>
                  <span className="key">F</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Rotate View</h4>
                  <span className="key">R</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Hide Panels + Tools</h4>
                  <span className="key">Tab</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-6 columns text-center">
                  <h4>Decrease Brush Size</h4>
                  <span className="key">[</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>Increase Brush Size</h4>
                  <span className="key">]</span>
                </div>
              </div>

              <hr />

              <h2 className="text-center">Workflow</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>Group Layers</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">G</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Image Size</h4>
                  <span className="key">
                    Opt <span className="key-alternate">(Alt)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">I</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>New Layer</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">N</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Save For Web</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Opt <span className="key-alternate">(Alt)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">S</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Fill with Background</h4>
                  <span className="key">
                    Opt <span className="key-alternate">(Alt)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Delete</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Fill with Foreground</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Delete</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Inverse Colors</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">I</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Inverse Selection</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">I</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Deselect</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">D</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Duplicate Layer</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">J</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Free Transform</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">T</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Hide Selection</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">H</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Stamp Visible</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Opt <span className="key-alternate">(Alt)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">E</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Merge Visible Layers</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">E</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Stamp Down Selected Layers</h4>
                  <span className="key">
                    Opt <span className="key-alternate">(Alt)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">E</span>
                </div>
              </div>
            </div>
          </PostFullContent>
        </article>
      </main>
      <BackToTop />
      <Footer />
    </Wrapper>
  </IndexLayout>
);

export default KBShortcuts;
